import { useState, useEffect } from "react";
import { 
  Activity, 
  Users, 
  Video, 
  Eye, 
  Zap, 
  TrendingUp,
  Clock,
  RefreshCw
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: string;
  subtitle?: string;
}

function StatCard({ title, value, icon, trend, subtitle }: StatCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {subtitle && (
          <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
        )}
        {trend && (
          <p className="text-xs text-emerald-600 flex items-center gap-1 mt-1">
            <TrendingUp className="h-3 w-3" />
            {trend}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

interface RealtimeActivity {
  id: string;
  user_nickname: string;
  video_title: string;
  action: string;
  time: string;
}

export default function AnalyticsDashboard() {
  const [stats, setStats] = useState({
    totalUsers: 0,
    todayUsers: 0,
    totalVideos: 0,
    totalViews: 0,
    todayViews: 0,
    aiTokensUsed: 0,
    qwenTokens: 0,
    deepseekTokens: 0,
  });
  const [activities, setActivities] = useState<RealtimeActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const fetchStats = async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Fetch all stats in parallel
      const [
        { count: totalUsers },
        { count: todayUsers },
        { count: totalVideos },
        { count: totalViews },
        { count: todayViews },
        { data: aiLogs },
      ] = await Promise.all([
        supabase.from("profiles").select("*", { count: "exact", head: true }),
        supabase.from("profiles").select("*", { count: "exact", head: true })
          .gte("created_at", today.toISOString()),
        supabase.from("videos").select("*", { count: "exact", head: true }),
        supabase.from("learning_records").select("*", { count: "exact", head: true }),
        supabase.from("learning_records").select("*", { count: "exact", head: true })
          .gte("created_at", today.toISOString()),
        supabase.from("ai_usage_log").select("provider, tokens_used"),
      ]);

      // Calculate AI token usage by provider
      let qwenTokens = 0;
      let deepseekTokens = 0;
      if (aiLogs && Array.isArray(aiLogs)) {
        aiLogs.forEach((log) => {
          if (log.provider === "qwen") {
            qwenTokens += log.tokens_used || 0;
          } else if (log.provider === "deepseek") {
            deepseekTokens += log.tokens_used || 0;
          }
        });
      }

      setStats({
        totalUsers: totalUsers || 0,
        todayUsers: todayUsers || 0,
        totalVideos: totalVideos || 0,
        totalViews: totalViews || 0,
        todayViews: todayViews || 0,
        aiTokensUsed: qwenTokens + deepseekTokens,
        qwenTokens,
        deepseekTokens,
      });

      // Fetch recent activities
      const { data: recentRecords } = await supabase
        .from("learning_records")
        .select(`
          id,
          created_at,
          profiles:user_id (nickname),
          videos:video_id (title)
        `)
        .order("created_at", { ascending: false })
        .limit(10);

      if (recentRecords) {
        setActivities(
          recentRecords.map((record: any) => ({
            id: record.id,
            user_nickname: record.profiles?.nickname || "匿名用户",
            video_title: record.videos?.title || "未知视频",
            action: "观看了",
            time: new Date(record.created_at).toLocaleTimeString("zh-CN"),
          }))
        );
      }

      setLastRefresh(new Date());
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();

    // Set up realtime subscription for learning_records
    const channel = supabase
      .channel("realtime-analytics")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "learning_records" },
        async (payload) => {
          // Fetch user and video info for the new record
          const { data: record } = await supabase
            .from("learning_records")
            .select(`
              id,
              created_at,
              profiles:user_id (nickname),
              videos:video_id (title)
            `)
            .eq("id", payload.new.id)
            .single();

          if (record) {
            setActivities((prev) => [
              {
                id: record.id,
                user_nickname: (record as any).profiles?.nickname || "匿名用户",
                video_title: (record as any).videos?.title || "未知视频",
                action: "观看了",
                time: new Date(record.created_at).toLocaleTimeString("zh-CN"),
              },
              ...prev.slice(0, 9),
            ]);
            
            // Update today's views
            setStats((prev) => ({
              ...prev,
              totalViews: prev.totalViews + 1,
              todayViews: prev.todayViews + 1,
            }));
          }
        }
      )
      .subscribe();

    // Auto refresh every 30 seconds
    const interval = setInterval(fetchStats, 30000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(interval);
    };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">实时监控</h1>
          <p className="text-sm text-muted-foreground">
            最后更新: {lastRefresh.toLocaleTimeString("zh-CN")}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchStats}>
          <RefreshCw className="h-4 w-4 mr-2" />
          刷新
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="总用户数"
          value={stats.totalUsers}
          icon={<Users className="h-4 w-4 text-muted-foreground" />}
          subtitle={`今日新增 ${stats.todayUsers}`}
        />
        <StatCard
          title="视频总数"
          value={stats.totalVideos}
          icon={<Video className="h-4 w-4 text-muted-foreground" />}
        />
        <StatCard
          title="总播放量"
          value={stats.totalViews}
          icon={<Eye className="h-4 w-4 text-muted-foreground" />}
          subtitle={`今日 ${stats.todayViews}`}
        />
        <StatCard
          title="AI Token 消耗"
          value={stats.aiTokensUsed.toLocaleString()}
          icon={<Zap className="h-4 w-4 text-muted-foreground" />}
          subtitle={`Qwen: ${stats.qwenTokens.toLocaleString()} | DeepSeek: ${stats.deepseekTokens.toLocaleString()}`}
        />
      </div>

      {/* AI Provider Status */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Zap className="h-5 w-5" />
            AI 服务状态
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex items-center gap-2">
              <Badge className="bg-emerald-600 hover:bg-emerald-700">主要</Badge>
              <span className="font-medium">Qwen (通义千问)</span>
              <span className="text-sm text-muted-foreground">
                - {stats.qwenTokens.toLocaleString()} tokens
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline">备用</Badge>
              <span className="font-medium">DeepSeek</span>
              <span className="text-sm text-muted-foreground">
                - {stats.deepseekTokens.toLocaleString()} tokens
              </span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            系统优先使用 Qwen API，当 Qwen 额度用尽或出错时自动切换到 DeepSeek
          </p>
        </CardContent>
      </Card>

      {/* Realtime Activity Feed */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="h-5 w-5" />
            实时动态
            <Badge variant="outline" className="ml-2 animate-pulse">
              LIVE
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {activities.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                暂无活动记录
              </p>
            ) : (
              activities.map((activity) => (
                <div
                  key={activity.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <Eye className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm">
                        <span className="font-medium">{activity.user_nickname}</span>
                        <span className="text-muted-foreground"> {activity.action} </span>
                        <span className="font-medium">{activity.video_title}</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    {activity.time}
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
